import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertCircle, ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { GrGameConfigDetail, GrRewardRuleResponse } from '@/lib/api';
import { useRewardRules } from '../hooks';
import { RuleFormDialog } from '../components';

export function AdminGameDetailPage() {
  const { gameConfigId } = useParams<{ gameConfigId: string }>();
  const [game, setGame] = useState<GrGameConfigDetail | null>(null);
  const [gameLoading, setGameLoading] = useState(true);
  const [gameError, setGameError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const {
    rules,
    loading: rulesLoading,
    error: rulesError,
    refresh: refreshRules,
  } = useRewardRules({ gameConfigId });

  const fetchGame = useCallback(async () => {
    if (!gameConfigId) return;
    const id = ++fetchIdRef.current;
    setGameLoading(true);
    setGameError(null);
    try {
      const result = await api.getGameRewardsGame(gameConfigId);
      if (fetchIdRef.current !== id) return;
      setGame(result);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setGameError(err instanceof Error ? err.message : 'Failed to load game');
    } finally {
      if (fetchIdRef.current === id) {
        setGameLoading(false);
      }
    }
  }, [gameConfigId]);

  useEffect(() => {
    fetchGame();
    return () => {
      fetchIdRef.current++;
    };
  }, [fetchGame]);

  // Rule dialog state
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<GrRewardRuleResponse | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const openCreateDialog = () => {
    setEditingRule(null);
    setRuleDialogOpen(true);
  };

  const openEditDialog = (rule: GrRewardRuleResponse) => {
    setEditingRule(rule);
    setRuleDialogOpen(true);
  };

  const handleDialogChange = (open: boolean) => {
    setRuleDialogOpen(open);
    if (!open) {
      setEditingRule(null);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      await api.deleteGameRewardsRule(ruleId);
      refreshRules();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete rule');
    }
  };

  // Group rules by game event for tree display
  const rulesByEvent = rules.reduce<Record<string, GrRewardRuleResponse[]>>((acc, rule) => {
    if (!acc[rule.gameEvent]) acc[rule.gameEvent] = [];
    acc[rule.gameEvent].push(rule);
    return acc;
  }, {});

  const error = gameError || rulesError;

  if (gameLoading) {
    return (
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error && !game) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <Link
          to="/a/arche_game_rewards/admin/games"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Games
        </Link>
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Compute a preview: reward = m * score + b
  const previewScore = game ? Math.round((game.minScore + game.maxScore) / 2) : 50;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_game_rewards/admin/games"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Games
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">{game?.displayName}</h1>
          {game && (
            <Badge variant={game.isActive ? 'default' : 'secondary'}>
              {game.isActive ? 'Active' : 'Inactive'}
            </Badge>
          )}
        </div>
        {game && (
          <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
            <p>
              {game.gameName} | Score range: {game.minScore}-{game.maxScore} | Key prefix:{' '}
              {game.apiKeyPrefix}
            </p>
            <p>
              Mode:{' '}
              <span className="font-medium text-foreground">
                {game.rewardMode === 'item_list' ? 'Item List' : 'Standard'}
              </span>
              {game.allowedItems && game.allowedItems.length > 0 && (
                <span> | Allowed items: {game.allowedItems.join(', ')}</span>
              )}
            </p>
          </div>
        )}
      </div>

      {(rulesError || deleteError) && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{rulesError || deleteError}</p>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium">Reward Rules</h2>
          <Button size="sm" onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Add Rule
          </Button>
        </div>

        {gameConfigId && (
          <RuleFormDialog
            open={ruleDialogOpen}
            onOpenChange={handleDialogChange}
            gameConfigId={gameConfigId}
            editingRule={editingRule}
            onSaved={refreshRules}
          />
        )}

        {rulesLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        ) : Object.keys(rulesByEvent).length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No reward rules defined. Click "Add Rule" to create one.
          </p>
        ) : (
          <div className="space-y-4">
            {Object.entries(rulesByEvent).map(([event, eventRules]) => (
              <Card key={event}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-mono">{event}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reward Type</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead>Formula</TableHead>
                        <TableHead>Max</TableHead>
                        <TableHead>Multipliers (B/S/G)</TableHead>
                        <TableHead className="w-20" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {eventRules.map((rule) => {
                        let formulaDisplay: React.ReactNode;
                        if (rule.steps && rule.steps.length > 0) {
                          formulaDisplay = <span>step-table ({rule.steps.length} steps)</span>;
                        } else {
                          const rawReward = Math.min(
                            Math.max(0, Math.round(rule.m * previewScore + rule.b)),
                            rule.maxRewardCount,
                          );
                          formulaDisplay = (
                            <>
                              {rule.m}*score + {rule.b}
                              <span className="text-muted-foreground ml-1">
                                (e.g. score={previewScore} = {rawReward})
                              </span>
                            </>
                          );
                        }
                        return (
                          <TableRow key={rule.ruleId}>
                            <TableCell>{rule.rewardType}</TableCell>
                            <TableCell className="font-mono text-xs">{rule.itemDefId}</TableCell>
                            <TableCell className="text-xs">{formulaDisplay}</TableCell>
                            <TableCell>{rule.steps ? '-' : rule.maxRewardCount}</TableCell>
                            <TableCell>
                              {rule.baseMultiplier !== 1 && (
                                <span className="text-muted-foreground">
                                  {rule.baseMultiplier}x /{' '}
                                </span>
                              )}
                              {rule.bronzeMultiplier}x / {rule.silverMultiplier}x /{' '}
                              {rule.goldMultiplier}x
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => openEditDialog(rule)}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete Reward Rule</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Are you sure you want to delete this reward rule? This
                                        action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDeleteRule(rule.ruleId)}
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
